import { Slider } from '@/components/ui/slider'
import { InputWithSuffix } from '@/components/ui/input-group'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'

import { useNumericFieldInput } from '../hooks/use-numeric-field-input'

interface NumericPresetFieldProps {
  label: string
  /**
   * The Kerning/Tracking popover's own `Label` node is hidden in Figma (20446:483097) — the tab
   * strip above already names the field, so a second heading is redundant. `label` still drives
   * `aria-label` and the error id either way. Left visible for every other consumer, whose Figma
   * bodies DO show it (`Text Offset`, `Start Position`).
   */
  hideLabel?: boolean
  value: number
  min: number
  max: number
  step: number
  presets: readonly number[]
  presetLabel: (preset: number) => string
  suffix: string
  onCommit: (next: number) => void
}

/**
 * Preset chips + slider + numeric input, with the inline error AC-6's screenshots specify
 * ("Display an error if range is out of the accepted range") rather than a silent clamp.
 *
 * One component for Size and for the Tracking/Kerning slot, so the three fields cannot drift apart
 * in validation, formatting or error copy — the Type panel spells the same ladder out three times.
 */
export function NumericPresetField({
  label,
  hideLabel = false,
  value,
  min,
  max,
  step,
  presets,
  presetLabel,
  suffix,
  onCommit,
}: NumericPresetFieldProps) {
  const { inputValue, error, commit, handleFocus, handleChange, handleBlur } = useNumericFieldInput(
    value,
    min,
    max,
    onCommit
  )
  const errorId = `text-on-path-${label.toLowerCase()}-error`
  const activePreset = presets.find((preset) => preset === value)

  return (
    <div className="flex flex-col gap-2">
      {!hideLabel && <span className="text-sm font-medium text-foreground">{label}</span>}
      <ToggleGroup
        type="single"
        variant="outline"
        size="sm"
        value={activePreset != null ? String(activePreset) : ''}
        onValueChange={(next) => {
          if (!next) return
          commit(Number(next))
        }}
        className="justify-start gap-2"
      >
        {presets.map((preset) => (
          <ToggleGroupItem
            key={preset}
            value={String(preset)}
            className="h-9 px-2 text-sm font-medium"
          >
            {presetLabel(preset)}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      {/*
        `items-center`, not `items-start`: the design puts the 6px rail on the input's own centre
        line. The `mt-2` this replaces was faking that alignment and left the slider sitting low.
      */}
      <div className="flex items-center gap-3">
        <Slider
          min={min}
          max={max}
          step={step}
          value={[value]}
          onValueChange={([next]) => commit(next ?? value)}
          aria-label={label}
          className="min-w-0 flex-1"
        />
        <div className="flex w-[100px] flex-col">
          <InputWithSuffix
            size="compact"
            type="text"
            value={inputValue}
            error={error != null}
            aria-label={`${label} value`}
            aria-describedby={error != null ? errorId : undefined}
            suffix={<span className="pl-1 text-xs text-muted-foreground">{suffix}</span>}
            onFocus={handleFocus}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {error != null && (
            <span id={errorId} role="alert" className="mt-1 text-xs text-red-500">
              {error}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
